import React, { useMemo, useState } from 'react';
import { List, Typography } from 'antd';
import classNames from 'classnames';
import styles from './index.less';
import Property from '@/components/Kanban/VisibilitySettings/Property';
import { FIELD_GROUPS, getFieldGroupKey } from '@/components/Kanban/VisibilitySettings/fieldGroups';
import { ExtraFieldTypeModel } from '@/typings/models/ExtraFields';
import { useIntl } from 'umi';

const { Text } = Typography;

type IFieldListProps = {
  fields: Array<string | ExtraFieldTypeModel>,
  isVisible: boolean,
  onToggle: (field: string, isVisible: boolean) => void,
  groupBy?: boolean
}
export default function FieldList({ fields, onToggle, isVisible, groupBy = false }: IFieldListProps) {
  const [animationClasses, setAnimationClasses] = useState<{ [field: string]: string }>({});
  const intl = useIntl();
  const handleToggle = (field, nextIsVisible) => {
    setAnimationClasses((prev) => ({
      ...prev,
      [field]: classNames(styles.fadeOut, {
        [styles.fadeOutForHiddenItems]: nextIsVisible,
      }),
    }));

    // Wait for the animation to finish before updating visibility
    setTimeout(() => {
      onToggle(field, nextIsVisible);
      setTimeout(() => {
        setAnimationClasses(prev => ({
          ...prev,
          [field]: '',
        }));
      }, 100);
    }, 100); // Match the CSS transition time (0.3s)
  };

  // Map of group keys to localized titles
  const titleMap: Record<string, { id: string; defaultMessage: string }> = {
    
    [FIELD_GROUPS.CUSTOMER]: {
      id: 'entity.customer.singular',
      defaultMessage: 'Customer',
    },
    [FIELD_GROUPS.LEAD]: {
      id: 'entity.lead.singular',
      defaultMessage: 'Lead',
    },
    [FIELD_GROUPS.APPOINTMENT]: {
      id: 'entity.appointment.singular',
      defaultMessage: 'Appointment',
    },
    [FIELD_GROUPS.OTHER]: {
      id: 'common.fields.other',
      defaultMessage: 'Other Fields',
    },
  };

  const titleForKey = (key: string) => {
    const entry = titleMap[key];
    if (entry) return intl.formatMessage(entry);
    return `${key.charAt(0).toUpperCase()}${key.slice(1)} Fields`;
  };

  const groupedFields = useMemo(() => {
    const getFieldLabel = (f: string | ExtraFieldTypeModel) =>
      typeof f === 'string' ? intl.formatMessage({
        id: `pages.lead.fields.${f}`,
        defaultMessage: f,
      }) : (f.label || '');

    // Standard fields first, then custom (extra) fields, each alpha-sorted
    const sortFields = (arr: Array<string | ExtraFieldTypeModel>) => {
      const alphaSort = (fields: Array<string | ExtraFieldTypeModel>) =>
        [...fields].sort((a, b) =>
          getFieldLabel(a).localeCompare(getFieldLabel(b), undefined, { sensitivity: 'base' }),
        );
      return [
        ...alphaSort(arr.filter(f => typeof f === 'string')),
        ...alphaSort(arr.filter(f => typeof f !== 'string')),
      ];
    };

    if (!groupBy) {
      // If you also want the flat list sorted when not grouped, uncomment:
      // return [{ key: 'all', title: null, fields: sortFields(fields) }];
      return [{ key: 'all', title: null, fields }];
    }

    // 1) Build groups
    const groups: { [key: string]: Array<string | ExtraFieldTypeModel> } = {};
    fields.forEach(field => {
      const groupKey = getFieldGroupKey(field);
      (groups[groupKey] ||= []).push(field);
    });

    // 2) Preferred group ordering, then alpha by title for unknowns
    const GROUP_ORDER: string[] = [
      FIELD_GROUPS.LEAD,
      FIELD_GROUPS.CUSTOMER,
      FIELD_GROUPS.APPOINTMENT,
      FIELD_GROUPS.OTHER,
    ];

    const entries = Object.entries(groups)
      .map(([key, fieldList]) => ({
        key,
        title: titleForKey(key),
        fields: sortFields(fieldList), // sort items inside each group
        _orderIdx: GROUP_ORDER.indexOf(key),
      }))
      .sort((a, b) => {
        const ai = a._orderIdx === -1 ? Number.MAX_SAFE_INTEGER : a._orderIdx;
        const bi = b._orderIdx === -1 ? Number.MAX_SAFE_INTEGER : b._orderIdx;
        if (ai !== bi) return ai - bi;
        // fallback alpha by title
        return a.title.localeCompare(b.title, undefined, { sensitivity: 'base' });
      })
      .map(({ _orderIdx, ...rest }) => rest); // strip helper

    return entries;
  }, [fields, groupBy]);

  const renderField = (field: string | ExtraFieldTypeModel) => {
    if (typeof field === 'string') {
      const groupTitle = !groupBy ? titleForKey(getFieldGroupKey(field)) : null;
      return (
        <List.Item key={field} onClick={() => handleToggle(field, !isVisible)}>
          <div style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
            <Property
              hasSort
              id={field}
              languageKey={field}
              groupTitle={groupTitle}
              className={animationClasses[field]}
              isVisible={isVisible}
            />
          </div>
        </List.Item>
      );
    }

    const id = `${field.type}:${field.uuid}`;
    const groupTitle = !groupBy ? titleForKey(getFieldGroupKey(field)) : null;
    return (
      <List.Item key={id} onClick={() => handleToggle(id, !isVisible)}>
        <div style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
          <Property
            hasSort
            id={id}
            title={field.label}
            className={animationClasses[id]}
            isVisible={isVisible}
            groupTitle={groupTitle}
            isCustomField
            isDragging={false}
          />
        </div>
      </List.Item>
    );
  };


  if (!groupBy) {
    return (
      <List
        bordered
        className={styles.listContainer}
        dataSource={fields}
        renderItem={renderField}
      />
    );
  }

  return (
    <div>
      {groupedFields.map(group => (
        <div key={group.key} style={{ marginBottom: '16px' }}>
          {group.title && (
            <Text strong style={{
              display: 'block',
              marginBottom: '8px',
              padding: '4px 8px',
              backgroundColor: '#f5f5f5',
              borderRadius: '4px',
            }}>
              {group.title}
            </Text>
          )}
          <List
            bordered
            className={styles.listContainer}
            dataSource={group.fields}
            renderItem={renderField}
          />
        </div>
      ))}
    </div>
  );
}
